<template>
  <div class="screen-viewer">
    <div class="screen-content" :style="contentStyle">
      <transition name="fade" mode="out-in">
        <div
          v-if="currentSlide && currentSlide.type === 'media'"
          :key="'media-' + currentSlideIndex"
          class="slide media-slide"
        >
          <img :src="currentSlideImage" :alt="currentSlide.title" class="slide-image" />
        </div>
        <div
          v-else-if="currentSlide && currentSlide.type === 'announcement'"
          :key="'ann-' + currentSlideIndex"
          class="slide announcement-slide"
          :style="{ backgroundColor: currentSlide.bgColor || '#1a1a2e' }"
        >
          <div class="announcement-content">
            <h1 class="announcement-title">{{ currentSlide.title }}</h1>
            <p class="announcement-text">{{ currentSlide.content }}</p>
          </div>
        </div>
      </transition>

      <div
        class="timer-bar"
        :style="{ animationDuration: currentDuration + 'ms' }"
        :key="currentSlideIndex"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import type { Screen, ScreenPlaylistItem } from '@/models'
import { fetchActiveAnnouncements } from '@/services/announcements'
import {
  fetchCompanyMedia,
  fetchScreenById,
  recordHeartbeat,
  recordSlideView,
} from '@/services/screens'
import { getDisplayUrl } from '@/services/storage'

const route = useRoute()

const currentSlideIndex = ref(0)
const slideInterval = ref(8000)
let interval: ReturnType<typeof setTimeout> | null = null
let heartbeatInterval: ReturnType<typeof setInterval> | null = null

interface Slide {
  type: 'media' | 'announcement'
  refId: string
  title: string
  content?: string
  fileKey?: string
  duration: number
  bgColor?: string
}

const slides = ref<Slide[]>([])
const urlCache = ref<Map<string, string>>(new Map())
const screenId = ref('')

const currentSlide = computed(() => slides.value[currentSlideIndex.value] ?? null)
const currentDuration = computed(() => currentSlide.value?.duration ?? 8000)

const currentSlideImage = computed(() => {
  if (!currentSlide.value?.fileKey) return ''
  return urlCache.value.get(currentSlide.value.fileKey) || ''
})

const contentStyle = computed(() => {
  if (currentSlide.value?.type === 'announcement') {
    return { backgroundColor: currentSlide.value.bgColor || '#1a1a2e' }
  }
  return {}
})

function placeholderSvg(label: string, w = 1920, h = 1080): string {
  return `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><rect fill="#1a1a2e" width="${w}" height="${h}"/><text fill="#e0e0e0" font-family="sans-serif" font-size="48" x="${w / 2}" y="${h / 2}" text-anchor="middle" dominant-baseline="middle">${label}</text></svg>`)}`
}

async function resolveImageUrl(key: string): Promise<string> {
  const cached = urlCache.value.get(key)
  if (cached) return cached
  const url = await getDisplayUrl(key)
  urlCache.value.set(key, url)
  return url
}

const enterFullscreen = () => {
  const elem = document.documentElement
  if (elem.requestFullscreen) {
    elem.requestFullscreen()
  } else if ((elem as any).webkitRequestFullscreen) {
    ;(elem as any).webkitRequestFullscreen()
  } else if ((elem as any).msRequestFullscreen) {
    ;(elem as any).msRequestFullscreen()
  }
}

async function sendHeartbeat() {
  if (!screenId.value) return
  try {
    await recordHeartbeat(screenId.value)
  } catch {
    // silently fail — viewer shouldn't break on network errors
  }
}

async function trackSlideView() {
  if (!screenId.value || !currentSlide.value) return
  try {
    await recordSlideView(
      screenId.value,
      currentSlideIndex.value,
      currentSlide.value.type,
      currentSlide.value.refId,
    )
  } catch {
    // silently fail
  }
}

async function init() {
  const rawId = route.params.id
  const sid = Array.isArray(rawId) ? (rawId[0] ?? '') : String(rawId ?? '')
  screenId.value = sid

  if (!sid) return

  try {
    const screenData: Screen = await fetchScreenById(sid)
    slideInterval.value = (screenData.slide_duration || 8) * 1000

    const [media, anns] = await Promise.all([
      fetchCompanyMedia(screenData.company_id),
      screenData.show_announcements && screenData.selected_announcement_ids?.length
        ? fetchActiveAnnouncements(screenData.company_id)
        : Promise.resolve([]),
    ])

    const slideDeck: Slide[] = []

    if (screenData.playlist_order && screenData.playlist_order.length > 0) {
      for (const entry of screenData.playlist_order) {
        if (entry.type === 'media') {
          const m = media.find((mm) => mm.id === entry.ref_id)
          if (m) {
            const resolved = await resolveImageUrl(m.file_key)
            if (resolved) {
              slideDeck.push({
                type: 'media',
                refId: m.id,
                title: m.title,
                fileKey: m.file_key,
                duration: (entry.duration ?? screenData.slide_duration) * 1000,
              })
            }
          }
        } else if (entry.type === 'announcement') {
          const a = anns.find((ann) => ann.id === entry.ref_id)
          if (a) {
            slideDeck.push({
              type: 'announcement',
              refId: a.id,
              title: a.title,
              content: a.content,
              duration: (entry.duration ?? screenData.slide_duration) * 1000,
              bgColor: screenData.announcement_bg_colors?.[a.id] || a.background_color,
            })
          }
        }
      }
    } else {
      for (const m of media) {
        const resolved = await resolveImageUrl(m.file_key)
        if (resolved) {
          slideDeck.push({
            type: 'media',
            refId: m.id,
            title: m.title,
            fileKey: m.file_key,
            duration: screenData.slide_duration * 1000,
          })
        }
      }
      if (screenData.show_announcements && screenData.selected_announcement_ids?.length) {
        for (const annId of screenData.selected_announcement_ids) {
          const a = anns.find((ann) => ann.id === annId)
          if (a) {
            slideDeck.push({
              type: 'announcement',
              refId: a.id,
              title: a.title,
              content: a.content,
              duration: screenData.slide_duration * 1000,
              bgColor: screenData.announcement_bg_colors?.[a.id] || a.background_color,
            })
          }
        }
      }
    }

    slides.value = slideDeck
  } catch {
    slides.value = [
      {
        type: 'media',
        refId: '',
        title: 'No Content Available',
        duration: 8000,
      },
    ]
  }

  if (slides.value.length === 0) {
    slides.value.push({
      type: 'media',
      refId: '',
      title: 'No Content',
      duration: 8000,
    })
  }

  // Start slideshow with self-scheduling setTimeout (respects per-slide durations)
  function scheduleNext() {
    interval = setTimeout(() => {
      advanceSlide()
      scheduleNext()
    }, currentDuration.value)
  }
  scheduleNext()

  // Heartbeat every 30 seconds
  sendHeartbeat()
  heartbeatInterval = setInterval(sendHeartbeat, 30000)
}

function advanceSlide() {
  currentSlideIndex.value = (currentSlideIndex.value + 1) % slides.value.length
  trackSlideView()
}

const contextmenuHandler = (e: Event) => e.preventDefault()

onMounted(() => {
  enterFullscreen()
  document.addEventListener('contextmenu', contextmenuHandler)
  init()
})

onUnmounted(() => {
  if (interval) clearTimeout(interval)
  if (heartbeatInterval) clearInterval(heartbeatInterval)
  document.removeEventListener('contextmenu', contextmenuHandler)
})
</script>

<style scoped>
.screen-viewer {
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background-color: #000;
  color: #fff;
}

.screen-content {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.slide {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.slide-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.media-slide {
  background: #000;
}

.announcement-content {
  text-align: center;
  padding: 80px;
  max-width: 80%;
}

.announcement-title {
  font-size: 64px;
  font-weight: 700;
  margin-bottom: 32px;
  line-height: 1.2;
}

.announcement-text {
  font-size: 32px;
  opacity: 0.9;
  line-height: 1.5;
}

.timer-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 4px;
  background: linear-gradient(90deg, #1890ff, #52c41a);
  animation: timer linear forwards;
}

@keyframes timer {
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.6s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
